import React, { useState, useRef } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { selectBilling } from '../../common/subscription/BillingSlice';
import { useSelector } from 'react-redux';
import { handleUpdateBillingInfo } from '../../common/subscription/SubscriptionService';
import {
  Stack,
  Typography,
  CircularProgress,
  TextField,
  Box,
  Button,
  FormControl,
  Autocomplete,
} from '@mui/material';
import { showSuccessSnackbar } from '../../common/snackbar/SnackbarActions';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import { CardElement, useRecurly } from '@recurly/react-recurly';
import RecurlyCountries from '../../assets/data/RecurlyCountries.json';
import { createFilterOptions } from '@mui/material/Autocomplete';

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('Required').nullable(),
  lastName: Yup.string().required('Required').nullable(),
  postalCode: Yup.string().required('Required').nullable(),
  country: Yup.mixed()
    .test('required', 'Required', (country) => {
      if (country) return true;
      else return false;
    })
    .test('format', 'Must be two character country code', (country) => {
      if (country) {
        if (
          country.alpha2Code.length === 2 &&
          /^[a-zA-Z]+$/.test(country.alpha2Code)
        ) {
          return true;
        }
      } else return false;
    }),
});

export default function UpdateBillingInfoForm({ callback = () => {} }) {
  const [errorMessage, setErrorMessage] = useState('');
  const billingInfo = useSelector(selectBilling);
  const recurly = useRecurly();
  const formRef = useRef();
  const countryPickerFilterOtions = createFilterOptions({
    stringify: (option) => option.alpha2Code + ' ' + option.englishShortName,
  });

  return (
    <Formik
      initialValues={{
        firstName: billingInfo.BillingFirstName,
        lastName: billingInfo.BillingLastName,
        postalCode: billingInfo.BillingAddressPostalCode,
        country: RecurlyCountries.find(
          (country) => country.alpha2Code === billingInfo.BillingAddressCountry
        ),
      }}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        recurly.token(formRef.current, (err, token) => {
          if (err) {
            console.error(err);
            if (err.code === 'validation') {
              setErrorMessage(
                'Error validating card information, please confirm the card number, expiration, and CVV'
              );
            } else {
              setErrorMessage(err.message);
            }
            setSubmitting(false);
          } else {
            handleUpdateBillingInfo(token.id)
              .then(() => {
                setSubmitting(false);
                showSuccessSnackbar('Billing details updated');
                callback();
              })
              .catch((err) => {
                let errMsg =
                  'An error has occurred, if it persists please contact us'; //Default message
                if (err.response !== undefined) {
                  errMsg =
                    err.response.data['Error'] && err.response.data['Error'];
                } else if (err.message !== undefined) {
                  errMsg = err.message;
                }
                console.error(err);
                console.error(errMsg);
                setErrorMessage(errMsg);
                setSubmitting(false);
              });
          }
        });
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting,
        setFieldValue,
      }) => {
        return (
          <Form ref={formRef}>
            <Stack alignItems='center' justifyContent='center'>
              <TextField
                name='firstName'
                type='text'
                label='First Name on Card'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.firstName}
                placeholder='First Name on Card'
                variant='standard'
                error={errors.firstName && touched.firstName}
                helperText={touched.firstName && errors.firstName}
                sx={{ m: 1, width: '20vw', minWidth: '35ch' }}
                inputProps={{ 'data-recurly': 'first_name', maxLength: 64 }}
              />
              <TextField
                name='lastName'
                type='text'
                label='Last Name on Card'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.lastName}
                placeholder='Last Name'
                variant='standard'
                error={errors.lastName && touched.lastName}
                helperText={touched.lastName && errors.lastName}
                sx={{ m: 1, width: '20vw', minWidth: '35ch' }}
                inputProps={{ 'data-recurly': 'last_name', maxLength: 64 }}
              />
              <FormControl
                sx={{ m: 1, width: '20vw', minWidth: '35ch' }}
                variant='standard'
              >
                <CardElement
                  style={{
                    fontFamily: 'Didact Gothic, sans-serif',
                    fontSize: '1.3rem',
                    fontColor: 'rgba(0, 0, 0, 0.87)',
                    placeholder: {
                      color: 'rgba(0, 0, 0, 0.54)',
                      content: { number: 'Card Number' },
                    },
                    invalid: { fontColor: '#fb2111' },
                  }}
                />
              </FormControl>
              <Autocomplete
                id='country-picker'
                sx={{ m: 1, width: '20vw', minWidth: '35ch' }}
                options={RecurlyCountries}
                autoHighlight
                getOptionLabel={(option) => option.alpha2Code}
                value={values.country}
                isOptionEqualToValue={(option, value) =>
                  option.alpha2Code === value.alpha2Code
                }
                renderOption={(props, option) => (
                  <Box
                    component='li'
                    sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                    {...props}
                  >
                    <img
                      loading='lazy'
                      width='20'
                      src={`https://flagcdn.com/w20/${option.alpha2Code.toLowerCase()}.png`}
                      srcSet={`https://flagcdn.com/w40/${option.alpha2Code.toLowerCase()}.png 2x`}
                      alt=''
                    />
                    {option.englishShortName} ({option.alpha2Code})
                  </Box>
                )}
                onChange={(e, value) => {
                  setFieldValue('country', value);
                }}
                onBlur={handleBlur}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name='country'
                    label='Billing Country'
                    variant='standard'
                    error={errors.country && touched.country}
                    helperText={touched.country && errors.country}
                    inputProps={{
                      ...params.inputProps,
                      'data-recurly': 'country',
                      maxLength: 15,
                    }}
                  />
                )}
                filterOptions={countryPickerFilterOtions}
              />
              <TextField
                name='postalCode'
                type='text'
                label='Billing Postal Code'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.postalCode}
                placeholder='Postal Code'
                variant='standard'
                error={errors.postalCode && touched.postalCode}
                helperText={touched.postalCode && errors.postalCode}
                sx={{ m: 1, width: '20vw', minWidth: '35ch' }}
                inputProps={{ 'data-recurly': 'postal_code', maxLength: 16 }}
              />
              {errorMessage && (
                <Typography sx={{ color: 'error.main' }}>
                  {errorMessage}
                </Typography>
              )}
              <Box sx={{ m: 1, position: 'relative' }}>
                <Button
                  sx={{ marginTop: 1 }}
                  type={'submit'}
                  disabled={isSubmitting}
                  variant='contained'
                >
                  <SaveIcon style={{ marginRight: '5' }} />
                  Save
                </Button>
                {isSubmitting && (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: 'secondary.main',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-9px',
                      marginLeft: '-12px',
                    }}
                  />
                )}
              </Box>
              <Button
                sx={{ marginTop: 1 }}
                type={'reset'}
                color='secondary'
                disabled={isSubmitting}
                variant='contained'
                onClick={callback}
              >
                <CancelIcon style={{ marginRight: '5' }} />
                Cancel
              </Button>
            </Stack>
          </Form>
        );
      }}
    </Formik>
  );
}
